import React from 'react';

import { Popover, PopoverRoot, usePopoverTriggerRef } from '@gitbook/spine-react';
import { usePersistentValue } from '@gitbook/util-react-hooks';

import { EditPopoverBody } from '../ContentEditor/link/EditLinkWrapper';
import type { InlineLinkProps } from '../ContentEditor/link/useInlineLinkProp';
import type { UIResolvedContentReference } from '../ContentReferences';

export interface EditLinkPopoverData {
    anchor: HTMLElement;
    kind?: 'link' | 'reference';
    resolved: UIResolvedContentReference;
    onPressEdit: () => void;
    onPressRemove: () => void;
    linkProps: InlineLinkProps | null;
    open: (options?: { delay?: number }) => void;
    close: (options?: { delay?: number }) => void;
}

interface EditLinkPopoverContextValue {
    open: (data: EditLinkPopoverData) => void;
    close: () => void;
}

const EditLinkPopoverContext = React.createContext<EditLinkPopoverContextValue | null>(null);

export function EditLinkPopoverProvider(props: { children: React.ReactNode }) {
    const { children } = props;

    const [data, setData] = React.useState<EditLinkPopoverData | null>(null);

    const open = React.useCallback((newData: EditLinkPopoverData) => {
        setData((current) => {
            // Update `data` only if the anchor changed to avoid unnecessary re-renders.
            if (current && current.anchor === newData.anchor) {
                return current;
            }
            return newData;
        });
    }, []);

    const close = React.useCallback(() => {
        setData(null);
    }, []);

    const contextValue = React.useMemo(() => ({ open, close }), [open, close]);

    const persistentData = usePersistentValue(data);

    return (
        <EditLinkPopoverContext.Provider value={contextValue}>
            {children}

            {persistentData && (
                <EditLinkPopoverRoot data={persistentData} isOpen={!!data} close={close} />
            )}
        </EditLinkPopoverContext.Provider>
    );
}

const EditLinkPopoverRoot = React.memo(function EditLinkPopoverRoot(props: {
    data: EditLinkPopoverData;
    isOpen: boolean;
    close: () => void;
}) {
    const { data, isOpen, close } = props;

    return (
        <PopoverRoot
            isOpen={isOpen}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    close();
                }
            }}
        >
            <EditLinkPopoverContent data={data} />
        </PopoverRoot>
    );
});

function EditLinkPopoverContent(props: { data: EditLinkPopoverData }) {
    const { data } = props;

    const triggerRef = usePopoverTriggerRef();

    React.useLayoutEffect(() => {
        // @ts-expect-error: `triggerRef` is readonly but we need to be able
        // to mutate it here as it's changing depending on the anchor.
        triggerRef.current = data.anchor;

        return () => {
            // @ts-expect-error: same as above.
            triggerRef.current = null;
        };
    }, [data.anchor, triggerRef]);

    return (
        <Popover
            detached
            offset={5}
            onMouseOver={() => data.open({ delay: 150 })}
            onMouseOut={() => data.close({ delay: 150 })}
        >
            <EditPopoverBody
                kind={data.kind}
                resolved={data.resolved}
                onPressEdit={data.onPressEdit}
                onPressRemove={data.onPressRemove}
                linkProps={data.linkProps}
            />
        </Popover>
    );
}

export function useEditLinkPopover() {
    const ctx = React.useContext(EditLinkPopoverContext);
    if (!ctx) {
        throw new Error('useEditLinkPopover must be used within EditLinkPopoverProvider');
    }
    return ctx;
}
